import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import employeeService from '../../../services/employeeService';
import employeeCodeSettingsService from '../../../services/employeeCodeSettingsService';

const ConvertToEmployeeModal = ({ applicant, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    email: applicant?.email || '',
    salary: '',
    hire_date: new Date().toISOString().split('T')[0],
    department: applicant?.department || '',
    position: applicant?.position || ''
  });
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [duplicateEmployee, setDuplicateEmployee] = useState(null);
  const [error, setError] = useState(null);
  const latestEmailRef = useRef('');

  useEffect(() => {
    if (isOpen && applicant) {
      const initialEmail = applicant.email || '';
      setFormData({
        employee_id: '',
        email: initialEmail,
        salary: '',
        hire_date: new Date().toISOString().split('T')[0],
        department: applicant.department || '',
        position: applicant.position || ''
      });
      setError(null);
      checkEmailCollision(initialEmail);

      employeeCodeSettingsService.previewNextCode().then(res => {
        if (res?.success && res.data?.next_code) {
          setFormData(prev => ({
            ...prev,
            employee_id: prev.employee_id || res.data.next_code
          }));
        }
      }).catch(err => console.error('Error fetching next employee code:', err));
    }
  }, [isOpen, applicant]);

  const checkEmailCollision = async (emailToCheck) => {
    const cleanEmail = (emailToCheck || '').trim().toLowerCase();
    latestEmailRef.current = cleanEmail;

    if (!cleanEmail) {
      setDuplicateEmployee(null);
      setCheckingEmail(false);
      return;
    }

    try {
      setCheckingEmail(true);
      const res = await employeeService.getEmployees({ email: cleanEmail });
      if (latestEmailRef.current !== cleanEmail) {
        return;
      }
      if (res?.success && Array.isArray(res.data)) {
        const match = res.data.find(emp => (emp.email || '').trim().toLowerCase() === cleanEmail);
        setDuplicateEmployee(match || null);
      } else {
        setDuplicateEmployee(null);
      }
    } catch (err) {
      console.error('Error checking email collision:', err);
      if (latestEmailRef.current === cleanEmail) {
        setDuplicateEmployee(null);
      }
    } finally {
      if (latestEmailRef.current === cleanEmail) {
        setCheckingEmail(false);
      }
    }
  };

  if (!isOpen || !applicant) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);

    if (name === 'email') {
      checkEmailCollision(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.employee_id.trim()) {
      setError('Employee ID is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email address is required');
      return;
    }
    if (duplicateEmployee) {
      setError(`An employee already exists with email '${formData.email.trim()}'. Please update the email address before converting.`);
      return;
    }
    if (!formData.salary || parseFloat(formData.salary) <= 0) {
      setError('Valid salary is required');
      return;
    }
    if (!formData.hire_date) {
      setError('Hire date is required');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare employee data from applicant and form inputs
      const employeeData = {
        employee_id: formData.employee_id.trim(),
        full_name: applicant.name || applicant.applicant_name,
        email: formData.email.trim(),
        phone: applicant.phone || '',
        department: formData.department || applicant.department || '',
        position: formData.position || applicant.position || '',
        hire_date: formData.hire_date,
        salary: parseFloat(formData.salary),
        status: 'active'
      };

      const result = await employeeService.createEmployee(employeeData);

      if (result.success) {
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
      } else {
        setError(result.error || 'Failed to create employee. Please try again.');
      }
    } catch (err) {
      console.error('Error converting to employee:', err);
      setError('An error occurred while converting to employee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const candidateName = applicant.name || applicant.applicant_name || 'Candidate';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-card rounded-xl border border-border w-full max-w-md shadow-xl overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted/20">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="UserPlus" className="text-primary" size={22} />
              Convert to Employee
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Convert <span className="font-semibold text-foreground">{candidateName}</span> to an active employee
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* General Error Alert */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-600 dark:text-red-400 text-sm flex items-start gap-2">
              <Icon name="AlertTriangle" size={18} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          {/* Email Collision Alert */}
          {duplicateEmployee && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-700 dark:text-amber-300 text-sm space-y-1">
              <div className="flex items-center gap-1.5 font-semibold">
                <Icon name="AlertCircle" size={16} />
                Existing Employee Email Collision
              </div>
              <p className="text-xs">
                An active employee already exists with email <span className="font-semibold">{formData.email}</span> (Employee: <span className="font-semibold">{duplicateEmployee.full_name}</span> - {duplicateEmployee.employee_id || duplicateEmployee.employee_code}).
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-200">
                Please edit the email field below to a unique email address before converting.
              </p>
            </div>
          )}

          {/* Duplicate Filing Warning */}
          {applicant.is_duplicate && !duplicateEmployee && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-blue-700 dark:text-blue-300 text-xs">
              <span className="font-semibold">Notice:</span> This applicant record was flagged as a duplicate filing ({applicant.duplicate_reason || 'Email'}). Verify candidate details below before proceeding.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Employee ID <span className="text-red-500">*</span>
            </label>
            <Input
              name="employee_id"
              value={formData.employee_id}
              onChange={handleChange}
              placeholder="e.g., EMP001"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Candidate Email <span className="text-red-500">*</span>
            </label>
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="e.g., candidate@example.com"
              required
            />
            {checkingEmail && <p className="text-xs text-muted-foreground mt-1">Checking email availability...</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Salary (₹) <span className="text-red-500">*</span>
            </label>
            <Input
              name="salary"
              type="number"
              value={formData.salary}
              onChange={handleChange}
              placeholder="e.g., 50000"
              min="0"
              step="0.01"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Hire Date <span className="text-red-500">*</span>
            </label>
            <Input
              name="hire_date"
              type="date"
              value={formData.hire_date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Department
              </label>
              <Input
                name="department"
                value={formData.department}
                onChange={handleChange}
                placeholder="Department"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Position
              </label>
              <Input
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="Position"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border mt-6">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              disabled={loading || !!duplicateEmployee || checkingEmail} 
              iconName="UserPlus" 
              iconPosition="left"
            >
              {loading ? 'Converting...' : 'Convert to Employee'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConvertToEmployeeModal;
